/**
 * Classify Stage Detail Component
 *
 * Shows intent classification results with:
 * - Intent category badge
 * - Confidence score bar
 * - Routing decision explanation
 */

import { Brain, Route, TrendingUp } from 'lucide-react';
import type { ClassifyStageData, IntentCategory } from '../../types/pipeline';

interface ClassifyStageDetailProps {
  data: ClassifyStageData | undefined;
}

const INTENT_COLORS: Record<
  IntentCategory,
  { bg: string; text: string; border: string }
> = {
  SIMPLE_NOTE: {
    bg: 'bg-accent-green/15',
    text: 'text-accent-green',
    border: 'border-accent-green/30',
  },
  SEARCH_ONLY: {
    bg: 'bg-accent-cyan/15',
    text: 'text-accent-cyan',
    border: 'border-accent-cyan/30',
  },
  SUMMARIZE: {
    bg: 'bg-accent-blue/15',
    text: 'text-accent-blue',
    border: 'border-accent-blue/30',
  },
  EXTRACT: {
    bg: 'bg-accent-orange/15',
    text: 'text-accent-orange',
    border: 'border-accent-orange/30',
  },
  REASON: {
    bg: 'bg-accent-purple/15',
    text: 'text-accent-purple',
    border: 'border-accent-purple/30',
  },
};

const INTENT_DESCRIPTIONS: Record<IntentCategory, string> = {
  SIMPLE_NOTE:
    'Simple note — direct save (no LLM, no worker, no review). Status flips straight to COMPLETED.',
  SEARCH_ONLY: 'Search through similar notes - RAG retrieval only',
  SUMMARIZE: 'Content summarization - full LLM processing',
  EXTRACT: 'Structured extraction - LLM with JSON output',
  REASON: 'Complex analysis - LLM with chain-of-thought',
};

export function ClassifyStageDetail({ data }: ClassifyStageDetailProps) {
  if (!data) {
    return (
      <div className="bg-bg-primary border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 text-text-muted">
          <Brain className="w-5 h-5 animate-pulse" />
          <span className="text-sm">Classification in progress...</span>
        </div>
      </div>
    );
  }

  const { intent, confidence, routingDecision } = data;
  const colors = INTENT_COLORS[intent];
  const description = INTENT_DESCRIPTIONS[intent];
  const confidencePercent = Math.round(confidence * 100);

  return (
    <div className="bg-bg-secondary border border-border rounded-lg p-4 space-y-4">
      {/* Header */}
      <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
        <Brain className="w-4 h-4" />
        CLASSIFY Stage Results
      </h3>

      {/* Intent Badge */}
      <div className="space-y-1">
        <label className="text-xs text-text-muted uppercase tracking-wide">
          Intent
        </label>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${colors.bg} ${colors.text} ${colors.border}`}
          >
            {intent}
          </span>
          <span className="text-xs text-text-muted">{description}</span>
        </div>
      </div>

      {/* Confidence Bar */}
      <div className="space-y-1">
        <label className="text-xs text-text-muted uppercase tracking-wide flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          Confidence
        </label>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-bg-primary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                confidencePercent >= 80
                  ? 'bg-accent-green'
                  : confidencePercent >= 60
                    ? 'bg-accent-yellow'
                    : 'bg-accent-red'
              }`}
              style={{ width: `${confidencePercent}%` }}
            />
          </div>
          <span className="text-xs font-mono font-medium text-text-primary w-10 text-right">
            {confidencePercent}%
          </span>
        </div>
      </div>

      {/* Routing Decision */}
      <div className="space-y-1">
        <label className="text-xs text-text-muted uppercase tracking-wide flex items-center gap-1">
          <Route className="w-3 h-3" />
          Routing Decision
        </label>
        <p className="text-xs font-medium text-text-primary bg-bg-primary px-3 py-2 rounded-sm border border-border">
          {routingDecision}
        </p>
      </div>

      {/* SIMPLE_NOTE — direct-save callout. Must stay in sync with
          INTENT_DESCRIPTIONS['SIMPLE_NOTE'] above and ROUTING_DECISIONS in
          types/pipeline.ts. Drift caught by Round 3 review. */}
      {intent === 'SIMPLE_NOTE' && (
        <div className="bg-accent-green/10 border border-accent-green/20 rounded-lg p-3 text-xs text-accent-green">
          <strong>Direct Save:</strong> No LLM, no worker, no review. Note row
          written directly to Postgres — status flips to COMPLETED in
          NotesService._route_by_intent() (service.py:379).
        </div>
      )}
    </div>
  );
}
